'use client';
import { useEffect, useState, MouseEvent } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import Toast from '@/components/Toast';
import {
  addBookmarkClient,
  deleteBookmarkClient,
  getBookmarkByWordClient,
} from '@/api/bookmarks/client';
import { links } from '@/utils/links';

interface BookmarkToggleButtonProps {
  wordId: number;
}

export default function BookmarkToggleButton({ wordId }: BookmarkToggleButtonProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  useEffect(() => {
    async function fetchBookmark() {
      try {
        const { bookmark } = await getBookmarkByWordClient({ wordId });
        setIsBookmarked(Boolean(bookmark));
        setBookmarkId(bookmark?.id ?? null);
      } catch (err) {
        console.error('Failed to get bookmark status', err);
      }
    }
    fetchBookmark();
  }, [wordId]);

  const toggleBookmark = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    try {
      if (isBookmarked) {
        if (!bookmarkId) return;
        await deleteBookmarkClient({ bookmarkId });
        setIsBookmarked(false);
        setBookmarkId(null);
        setToastMessage('Removed from bookmarks');
        setToastType('info');
      } else {
        const { bookmark } = await addBookmarkClient({ wordId });
        setIsBookmarked(true);
        setBookmarkId(bookmark.id);
        setToastMessage('Added to bookmarks');
        setToastType('success');
      }
    } catch (err) {
      if ((err as { status?: number }).status === 401) {
        router.push(links.login({ returnTo: pathname }));
        return;
      }
      setToastMessage('Failed to update bookmark');
      setToastType('error');
      console.error('Failed to toggle bookmark', err);
    } finally {
      setLoading(false);
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  return (
    <>
      <button
        onClick={toggleBookmark}
        disabled={loading}
        className={`relative p-2 rounded-full bg-white/80 backdrop-blur-sm transition-colors hover:bg-leaf-green hover:text-white disabled:opacity-50 ${
          isBookmarked ? 'text-leaf-green' : 'text-gray-600'
        }`}
        title={isBookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
      >
        {loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
        ) : isBookmarked ? (
          <BookmarkCheck className="h-4 w-4" />
        ) : (
          <Bookmark className="h-4 w-4" />
        )}
      </button>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage(null)} />
      )}
    </>
  );
}
